import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  useDisclosure,
  Input,
  Textarea,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  Spacer,
} from '@nextui-org/react';
import { useEffect, useMemo, useState } from 'react';
import { XCircle } from 'react-bootstrap-icons';
import { IoCheckmark, IoChevronDownOutline } from 'react-icons/io5';
import { TbNotes } from 'react-icons/tb';
import Editor from 'react-simple-wysiwyg';

export function PopupWithOneInput({
  open,
  headerTitle = 'Title',
  bodyText = '',
  inputOneInitialValue = '',
  inputOneLabel = 'First Input Label',
  inputOnePlaceholder = 'First Input Placeholder',
  inputOneValidator,
  inputOneAsTextAreaInsteadOfSingleInput = false,
  buttonOneText = 'Cancel',
  buttonTwoText = 'Submit',
  onButtonOneClick,
  onButtonTwoClick,
  onInputOneChange,
  buttonOneColor = 'default',
  buttonTwoColor = 'success',
  confirmAction = false,
}: {
  open: boolean;
  headerTitle?: string;
  bodyText?: string;
  inputOneInitialValue?: string;
  inputOneLabel: string;
  inputOnePlaceholder: string;
  inputOneValidator?: (e: string) => boolean;
  inputOneAsTextAreaInsteadOfSingleInput?: boolean;
  buttonOneText?: string;
  buttonTwoText?: string;
  onButtonOneClick?: any;
  onButtonTwoClick: any;
  onInputOneChange?: any;
  buttonOneColor?:
    | 'default'
    | 'success'
    | 'danger'
    | 'primary'
    | 'secondary'
    | 'warning'
    | undefined;
  buttonTwoColor?:
    | 'default'
    | 'success'
    | 'danger'
    | 'primary'
    | 'secondary'
    | 'warning'
    | undefined;
  confirmAction?: boolean;
}) {
  const { isOpen, onOpenChange } = useDisclosure({
    isOpen: open,
  });
  const [showConfirmation, setShowConfirmation] = useState(
    false && confirmAction,
  );
  const [inputValue, setInputValue] = useState(inputOneInitialValue);
  const [buttonTwoLoading, setButtonTwoLoading] = useState(false);

  // Add keyboard event listener to call the onButtonTwoClick function when the user presses the Enter key

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        if (confirmAction) {
          setShowConfirmation(true);
          return;
        }
        setButtonTwoLoading(true);
        onButtonTwoClick(inputValue);
        setButtonTwoLoading(false);
        setInputValue('');
      }
    };
    if (isOpen) document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [inputValue, confirmAction, onButtonTwoClick]);

  return (
    <>
      <PopupWithConfirmation
        open={showConfirmation}
        headerTitle={headerTitle + '?'}
        bodyText="Are you sure you want to continue?"
        buttonOneText="No"
        buttonTwoText="Yes"
        onButtonOneClick={() => {
          setShowConfirmation(false);
        }}
        onButtonTwoClick={() => {
          setShowConfirmation(false);
          onButtonTwoClick(inputValue);
        }}
      />
      <Modal
        backdrop="opaque"
        onOpenChange={onOpenChange}
        isOpen={isOpen}
        placement="top-center"
        size="lg"
        shadow="lg"
        hideCloseButton
      >
        <ModalContent>
          <ModalHeader className="flex flex-col gap-1">
            {headerTitle}
          </ModalHeader>
          <ModalBody>
            {bodyText}
            {inputOneAsTextAreaInsteadOfSingleInput ? (
              <Textarea
                autoFocus
                endContent={
                  <TbNotes className="text-2xl text-default-400 pointer-events-none flex-shrink-0" />
                }
                label={inputOneLabel}
                placeholder={inputOnePlaceholder}
                variant="bordered"
                isInvalid={inputOneValidator && inputOneValidator(inputValue)}
                color={inputValue.length > 0 ? 'success' : 'danger'}
                value={inputValue}
                onChange={(e) => {
                  setInputValue(e.target.value);
                  onInputOneChange && onInputOneChange(e.target.value);
                }}
              />
            ) : (
              <Input
                autoFocus
                endContent={
                  <TbNotes className="text-2xl text-default-400 pointer-events-none flex-shrink-0" />
                }
                label={inputOneLabel}
                placeholder={inputOnePlaceholder}
                variant="bordered"
                isInvalid={inputOneValidator && inputOneValidator(inputValue)}
                color={inputValue.length > 0 ? 'success' : 'danger'}
                value={inputValue}
                onChange={(e) => {
                  setInputValue(e.target.value);
                  onInputOneChange && onInputOneChange(e.target.value);
                }}
              />
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              color={buttonOneColor || 'default'}
              variant="flat"
              onPress={() => {
                onButtonOneClick && onButtonOneClick();
              }}
            >
              {buttonOneText}
            </Button>
            <Button
              color={buttonTwoColor || 'success'}
              isLoading={buttonTwoLoading}
              onPress={async () => {
                if (confirmAction) {
                  setShowConfirmation(true);
                  return;
                }
                setButtonTwoLoading(true);
                await onButtonTwoClick(inputValue);
                setButtonTwoLoading(false);
                setInputValue('');
              }}
            >
              {buttonTwoText}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}

export function PopupWithTwoInput({
  open,
  headerTitle = 'Title',
  bodyText = '',
  inputOneDir = 'ltr',
  inputOneInitialValue = '',
  inputOneLabel = 'First Input Label',
  inputOnePlaceholder = 'First Input Placeholder',
  inputOneValidator,
  inputOneAsTextAreaInsteadOfSingleInput = false,
  inputTwoDir = 'ltr',
  inputTwoInitialValue = '',
  inputTwoLabel = 'First Input Label',
  inputTwoPlaceholder = 'First Input Placeholder',
  inputTwoValidator,
  inputTwoAsTextAreaInsteadOfSingleInput = false,
  buttonOneText = 'Cancel',
  buttonTwoText = 'Submit',
  buttonExtraText,
  inputOneAsHtmlEditor = false,
  inputTwoAsHtmlEditor = false,
  onButtonOneClick,
  onButtonTwoClick,
  onButtonExtraClick,
  onInputOneChange,
  onInputTwoChange,
  buttonOneColor = 'default',
  buttonTwoColor = 'success',
  buttonExtraColor = 'default',
  confirmAction = false,
  clearOnSubmit = true,
}: {
  open: boolean;
  headerTitle?: string;
  bodyText?: string;
  inputOneDir?: 'ltr' | 'rtl';
  inputOneInitialValue?: string;
  inputOneLabel: string;
  inputOnePlaceholder: string;
  inputOneValidator?: (e: string) => boolean;
  inputOneAsTextAreaInsteadOfSingleInput?: boolean;
  inputOneAsHtmlEditor?: boolean;
  inputTwoDir?: 'ltr' | 'rtl';
  inputTwoInitialValue?: string;
  inputTwoLabel: string;
  inputTwoPlaceholder: string;
  inputTwoAsHtmlEditor?: boolean;
  inputTwoValidator?: (e: string) => boolean;
  inputTwoAsTextAreaInsteadOfSingleInput?: boolean;
  buttonOneText?: string;
  buttonTwoText?: string;
  buttonExtraText?: string;
  onButtonOneClick?: any;
  onButtonTwoClick: any;
  onButtonExtraClick?: (
    input1: string,
    setInput1: (input1: string) => void,
    input2: string,
    setInput2: (input2: string) => void,
  ) => void;

  onInputOneChange?: any;
  onInputTwoChange?: any;
  buttonOneColor?:
    | 'default'
    | 'success'
    | 'danger'
    | 'primary'
    | 'secondary'
    | 'warning'
    | undefined;
  buttonTwoColor?:
    | 'default'
    | 'success'
    | 'danger'
    | 'primary'
    | 'secondary'
    | 'warning'
    | undefined;
  buttonExtraColor?:
    | 'default'
    | 'success'
    | 'danger'
    | 'primary'
    | 'secondary'
    | 'warning'
    | undefined;
  confirmAction?: boolean;
  clearOnSubmit?: boolean;
}) {
  const { isOpen, onOpenChange } = useDisclosure({
    isOpen: open,
  });
  const [showConfirmation, setShowConfirmation] = useState(
    false && confirmAction,
  );
  const [input1Value, setInput1Value] = useState(inputOneInitialValue);
  const [input2Value, setInput2Value] = useState(inputTwoInitialValue);
  const [buttonTwoLoading, setButtonTwoLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      const firstInput = document.querySelector('input, textarea');
      if (firstInput) {
        (firstInput as any).focus();
      }
    }
  }, [isOpen]);
  return (
    <>
      <PopupWithConfirmation
        open={showConfirmation}
        headerTitle={headerTitle + '?'}
        bodyText="Are you sure you want to continue?"
        buttonOneText="No"
        buttonTwoText="Yes"
        onButtonOneClick={() => {
          setShowConfirmation(false);
        }}
        onButtonTwoClick={() => {
          setShowConfirmation(false);
          onButtonTwoClick(input1Value, input2Value);
        }}
      />
      <Modal
        backdrop="opaque"
        onOpenChange={onOpenChange}
        isOpen={isOpen}
        placement="top-center"
        size={inputOneAsHtmlEditor || inputTwoAsHtmlEditor ? '5xl' : 'lg'}
        shadow="lg"
        hideCloseButton
      >
        <ModalContent>
          <ModalHeader className="flex flex-col gap-1">
            {headerTitle}
          </ModalHeader>
          <ModalBody>
            {bodyText}
            <div
              style={{
                display: 'flex',
                flexDirection:
                  inputOneAsHtmlEditor || inputTwoAsHtmlEditor
                    ? 'row'
                    : 'column',
                maxHeight: '60vh',
                minHeight:
                  inputOneAsHtmlEditor || inputTwoAsHtmlEditor ? '40vh' : '',
                justifyContent: 'space-between',
                gap: '1rem',
              }}
            >
              {inputOneAsTextAreaInsteadOfSingleInput ? (
                inputOneAsHtmlEditor ? (
                  <Editor
                    value={input1Value}
                    style={{
                      overflow: 'scroll',
                    }}
                    dir={inputOneDir}
                    onChange={(e) => {
                      setInput1Value(e.target.value);
                      onInputOneChange && onInputOneChange(e.target.value);
                    }}
                  />
                ) : (
                  <Textarea
                    autoFocus
                    endContent={
                      <TbNotes className="text-2xl text-default-400 pointer-events-none flex-shrink-0" />
                    }
                    dir={inputOneDir}
                    label={inputOneLabel}
                    placeholder={inputOnePlaceholder}
                    variant="bordered"
                    isInvalid={
                      inputOneValidator && inputOneValidator(input1Value)
                    }
                    color={input1Value.length > 0 ? 'success' : 'danger'}
                    value={input1Value}
                    onChange={(e) => {
                      setInput1Value(e.target.value);
                      onInputOneChange && onInputOneChange(e.target.value);
                    }}
                  />
                )
              ) : (
                <Input
                  autoFocus
                  endContent={
                    <TbNotes className="text-2xl text-default-400 pointer-events-none flex-shrink-0" />
                  }
                  dir={inputOneDir}
                  label={inputOneLabel}
                  placeholder={inputOnePlaceholder}
                  variant="bordered"
                  isInvalid={
                    inputOneValidator && inputOneValidator(input1Value)
                  }
                  color={input1Value.length > 0 ? 'success' : 'danger'}
                  value={input1Value}
                  onChange={(e) => {
                    setInput1Value(e.target.value);
                    onInputOneChange && onInputOneChange(e.target.value);
                  }}
                />
              )}
              {inputTwoAsTextAreaInsteadOfSingleInput ? (
                inputTwoAsHtmlEditor ? (
                  <Editor
                    value={input2Value}
                    dir={inputTwoDir}
                    onChange={(e) => {
                      setInput2Value(e.target.value);
                      onInputTwoChange && onInputTwoChange(e.target.value);
                    }}
                  />
                ) : (
                  <Textarea
                    autoFocus
                    endContent={
                      <TbNotes className="text-2xl text-default-400 pointer-events-none flex-shrink-0" />
                    }
                    dir={inputTwoDir}
                    label={inputTwoLabel}
                    placeholder={inputTwoPlaceholder}
                    variant="bordered"
                    isInvalid={
                      inputTwoValidator && inputTwoValidator(input2Value)
                    }
                    color={
                      !inputTwoValidator || input2Value.length > 0
                        ? 'default'
                        : 'danger'
                    }
                    value={input2Value}
                    onChange={(e) => {
                      setInput2Value(e.target.value);
                      onInputTwoChange && onInputTwoChange(e.target.value);
                    }}
                  />
                )
              ) : (
                <Input
                  autoFocus
                  endContent={
                    <TbNotes className="text-2xl text-default-400 pointer-events-none flex-shrink-0" />
                  }
                  dir={inputTwoDir}
                  label={inputTwoLabel}
                  placeholder={inputTwoPlaceholder}
                  variant="bordered"
                  isInvalid={
                    inputTwoValidator && inputTwoValidator(input2Value)
                  }
                  color={
                    !inputTwoValidator || input2Value.length > 0
                      ? 'default'
                      : 'danger'
                  }
                  value={input2Value}
                  onChange={(e) => {
                    setInput2Value(e.target.value);
                    onInputTwoChange && onInputTwoChange(e.target.value);
                  }}
                />
              )}
            </div>
          </ModalBody>
          <ModalFooter className="justify-between">
            <Button
              color={buttonOneColor || 'default'}
              variant="flat"
              onPress={() => {
                onButtonOneClick && onButtonOneClick();
              }}
            >
              {buttonOneText}
            </Button>{' '}
            {buttonExtraText && onButtonExtraClick && (
              <Button
                color={buttonExtraColor || 'default'}
                variant="light"
                onPress={() => {
                  onButtonExtraClick &&
                    onButtonExtraClick(
                      input1Value,
                      setInput1Value,
                      input2Value,
                      setInput2Value,
                    );
                }}
              >
                {buttonExtraText}
              </Button>
            )}
            <Button
              color={buttonTwoColor || 'success'}
              isLoading={buttonTwoLoading}
              onPress={async () => {
                if (confirmAction) {
                  setShowConfirmation(true);
                  return;
                }
                setButtonTwoLoading(true);
                await onButtonTwoClick(input1Value, input2Value);
                setButtonTwoLoading(false);
                if (clearOnSubmit) {
                  setInput1Value('');
                  setInput2Value('');
                }
              }}
            >
              {buttonTwoText}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}

export function PopupWithThreeInput({
  open,
  headerTitle = 'Title',
  bodyText = '',
  inputOneDir = 'ltr',
  inputOneInitialValue = '',
  inputOneLabel = 'First Input Label',
  inputOnePlaceholder = 'First Input Placeholder',
  inputOneValidator,
  inputOneAsTextAreaInsteadOfSingleInput = false,
  inputTwoDir = 'ltr',
  inputTwoInitialValue = '',
  inputTwoLabel = 'First Input Label',
  inputTwoPlaceholder = 'First Input Placeholder',
  inputTwoValidator,
  inputTwoAsTextAreaInsteadOfSingleInput = false,
  inputThreeDir = 'ltr',
  inputThreeInitialValue = '',
  inputThreeLabel = 'First Input Label',
  inputThreePlaceholder = 'First Input Placeholder',
  inputThreeValidator,
  inputThreeAsTextAreaInsteadOfSingleInput = false,
  buttonOneText = 'Cancel',
  buttonTwoText = 'Submit',
  buttonExtraText,
  inputOneAsHtmlEditor = false,
  inputTwoAsHtmlEditor = false,
  inputThreeAsHtmlEditor = false,
  onButtonOneClick,
  onButtonTwoClick,
  onButtonExtraClick,
  onInputOneChange,
  onInputTwoChange,
  onInputThreeChange,
  buttonOneColor = 'default',
  buttonTwoColor = 'success',
  buttonExtraColor = 'default',
  confirmAction = false,
  clearOnSubmit = true,
}: {
  open: boolean;
  headerTitle?: string;
  bodyText?: string;
  inputOneDir?: 'ltr' | 'rtl';
  inputOneInitialValue?: string;
  inputOneLabel: string;
  inputOnePlaceholder: string;
  inputOneValidator?: (e: string) => boolean;
  inputOneAsTextAreaInsteadOfSingleInput?: boolean;
  inputOneAsHtmlEditor?: boolean;
  inputTwoDir?: 'ltr' | 'rtl';
  inputTwoInitialValue?: string;
  inputTwoLabel: string;
  inputTwoPlaceholder: string;
  inputTwoAsHtmlEditor?: boolean;
  inputTwoValidator?: (e: string) => boolean;
  inputTwoAsTextAreaInsteadOfSingleInput?: boolean;
  inputThreeDir?: 'ltr' | 'rtl';
  inputThreeInitialValue?: string;
  inputThreeLabel: string;
  inputThreePlaceholder: string;
  inputThreeAsHtmlEditor?: boolean;
  inputThreeValidator?: (e: string) => boolean;
  inputThreeAsTextAreaInsteadOfSingleInput?: boolean;
  buttonOneText?: string;
  buttonTwoText?: string;
  buttonExtraText?: string;
  onButtonOneClick?: any;
  onButtonTwoClick: any;
  onButtonExtraClick?: (
    input1: string,
    setInput1: (input1: string) => void,
    input2: string,
    setInput2: (input2: string) => void,
    input3: string,
    setInput3: (input3: string) => void,
  ) => void;
  onInputOneChange?: any;
  onInputTwoChange?: any;
  onInputThreeChange?: any;
  buttonOneColor?:
    | 'default'
    | 'success'
    | 'danger'
    | 'primary'
    | 'secondary'
    | 'warning'
    | undefined;
  buttonTwoColor?:
    | 'default'
    | 'success'
    | 'danger'
    | 'primary'
    | 'secondary'
    | 'warning'
    | undefined;
  buttonExtraColor?:
    | 'default'
    | 'success'
    | 'danger'
    | 'primary'
    | 'secondary'
    | 'warning'
    | undefined;
  confirmAction?: boolean;
  clearOnSubmit?: boolean;
}) {
  const { isOpen, onOpenChange } = useDisclosure({
    isOpen: open,
  });
  const [showConfirmation, setShowConfirmation] = useState(
    false && confirmAction,
  );
  const [input1Value, setInput1Value] = useState(inputOneInitialValue);
  const [input2Value, setInput2Value] = useState(inputTwoInitialValue);
  const [input3Value, setInput3Value] = useState(inputThreeInitialValue);

  const [buttonTwoLoading, setButtonTwoLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      const firstInput = document.querySelector('input, textarea');
      if (firstInput) {
        (firstInput as any).focus();
      }
    }
  }, [isOpen]);
  return (
    <>
      <PopupWithConfirmation
        open={showConfirmation}
        headerTitle={headerTitle + '?'}
        bodyText="Are you sure you want to continue?"
        buttonOneText="No"
        buttonTwoText="Yes"
        onButtonOneClick={() => {
          setShowConfirmation(false);
        }}
        onButtonTwoClick={() => {
          setShowConfirmation(false);
          onButtonTwoClick(input1Value, input2Value, input3Value);
        }}
      />
      <Modal
        backdrop="opaque"
        onOpenChange={onOpenChange}
        isOpen={isOpen}
        placement="top-center"
        size={inputOneAsHtmlEditor || inputTwoAsHtmlEditor ? '5xl' : 'lg'}
        shadow="lg"
        hideCloseButton
      >
        <ModalContent>
          <ModalHeader className="flex flex-col gap-1">
            {headerTitle}
          </ModalHeader>
          <ModalBody>
            {bodyText}
            <div
              style={{
                display: 'flex',
                flexDirection:
                  inputOneAsHtmlEditor ||
                  inputTwoAsHtmlEditor ||
                  inputThreeAsHtmlEditor
                    ? 'row'
                    : 'column',
                maxHeight: '60vh',
                minHeight:
                  inputOneAsHtmlEditor || inputTwoAsHtmlEditor ? '40vh' : '',
                justifyContent: 'space-between',
                gap: '1rem',
              }}
            >
              {inputOneAsTextAreaInsteadOfSingleInput ? (
                inputOneAsHtmlEditor ? (
                  <Editor
                    value={input1Value}
                    style={{
                      overflow: 'scroll',
                    }}
                    dir={inputOneDir}
                    onChange={(e) => {
                      setInput1Value(e.target.value);
                      onInputOneChange && onInputOneChange(e.target.value);
                    }}
                  />
                ) : (
                  <Textarea
                    autoFocus
                    endContent={
                      <TbNotes className="text-2xl text-default-400 pointer-events-none flex-shrink-0" />
                    }
                    dir={inputOneDir}
                    label={inputOneLabel}
                    placeholder={inputOnePlaceholder}
                    variant="bordered"
                    isInvalid={
                      inputOneValidator && inputOneValidator(input1Value)
                    }
                    color={input1Value.length > 0 ? 'success' : 'danger'}
                    value={input1Value}
                    onChange={(e) => {
                      setInput1Value(e.target.value);
                      onInputOneChange && onInputOneChange(e.target.value);
                    }}
                  />
                )
              ) : (
                <Input
                  autoFocus
                  endContent={
                    <TbNotes className="text-2xl text-default-400 pointer-events-none flex-shrink-0" />
                  }
                  dir={inputOneDir}
                  label={inputOneLabel}
                  placeholder={inputOnePlaceholder}
                  variant="bordered"
                  isInvalid={
                    inputOneValidator && inputOneValidator(input1Value)
                  }
                  color={input1Value.length > 0 ? 'success' : 'danger'}
                  value={input1Value}
                  onChange={(e) => {
                    setInput1Value(e.target.value);
                    onInputOneChange && onInputOneChange(e.target.value);
                  }}
                />
              )}
              {inputTwoAsTextAreaInsteadOfSingleInput ? (
                inputTwoAsHtmlEditor ? (
                  <Editor
                    value={input2Value}
                    dir={inputTwoDir}
                    onChange={(e) => {
                      setInput2Value(e.target.value);
                      onInputTwoChange && onInputTwoChange(e.target.value);
                    }}
                  />
                ) : (
                  <Textarea
                    autoFocus
                    endContent={
                      <TbNotes className="text-2xl text-default-400 pointer-events-none flex-shrink-0" />
                    }
                    dir={inputTwoDir}
                    label={inputTwoLabel}
                    placeholder={inputTwoPlaceholder}
                    variant="bordered"
                    isInvalid={
                      inputTwoValidator && inputTwoValidator(input2Value)
                    }
                    color={
                      !inputTwoValidator || input2Value.length > 0
                        ? 'default'
                        : 'danger'
                    }
                    value={input2Value}
                    onChange={(e) => {
                      setInput2Value(e.target.value);
                      onInputTwoChange && onInputTwoChange(e.target.value);
                    }}
                  />
                )
              ) : (
                <Input
                  autoFocus
                  endContent={
                    <TbNotes className="text-2xl text-default-400 pointer-events-none flex-shrink-0" />
                  }
                  dir={inputTwoDir}
                  label={inputTwoLabel}
                  placeholder={inputTwoPlaceholder}
                  variant="bordered"
                  isInvalid={
                    inputTwoValidator && inputTwoValidator(input2Value)
                  }
                  color={
                    !inputTwoValidator || input2Value.length > 0
                      ? 'default'
                      : 'danger'
                  }
                  value={input2Value}
                  onChange={(e) => {
                    setInput2Value(e.target.value);
                    onInputTwoChange && onInputTwoChange(e.target.value);
                  }}
                />
              )}
              {inputThreeAsTextAreaInsteadOfSingleInput ? (
                inputThreeAsHtmlEditor ? (
                  <Editor
                    value={input3Value}
                    dir={inputThreeDir}
                    onChange={(e) => {
                      setInput3Value(e.target.value);
                      onInputThreeChange && onInputThreeChange(e.target.value);
                    }}
                  />
                ) : (
                  <Textarea
                    autoFocus
                    endContent={
                      <TbNotes className="text-2xl text-default-400 pointer-events-none flex-shrink-0" />
                    }
                    dir={inputThreeDir}
                    label={inputThreeLabel}
                    placeholder={inputThreePlaceholder}
                    variant="bordered"
                    isInvalid={
                      inputThreeValidator && inputThreeValidator(input3Value)
                    }
                    color={
                      !inputThreeValidator || input3Value.length > 0
                        ? 'default'
                        : 'danger'
                    }
                    value={input3Value}
                    onChange={(e) => {
                      setInput3Value(e.target.value);
                      onInputThreeChange && onInputThreeChange(e.target.value);
                    }}
                  />
                )
              ) : (
                <Input
                  autoFocus
                  endContent={
                    <TbNotes className="text-2xl text-default-400 pointer-events-none flex-shrink-0" />
                  }
                  dir={inputThreeDir}
                  label={inputThreeLabel}
                  placeholder={inputThreePlaceholder}
                  variant="bordered"
                  isInvalid={
                    inputThreeValidator && inputThreeValidator(input3Value)
                  }
                  color={
                    !inputThreeValidator || input3Value.length > 0
                      ? 'default'
                      : 'danger'
                  }
                  value={input3Value}
                  onChange={(e) => {
                    setInput3Value(e.target.value);
                    onInputThreeChange && onInputThreeChange(e.target.value);
                  }}
                />
              )}
            </div>
          </ModalBody>
          <ModalFooter className="justify-between">
            <Button
              color={buttonOneColor || 'default'}
              variant="flat"
              onPress={() => {
                onButtonOneClick && onButtonOneClick();
              }}
            >
              {buttonOneText}
            </Button>{' '}
            {buttonExtraText && onButtonExtraClick && (
              <Button
                color={buttonExtraColor || 'default'}
                variant="light"
                onPress={() => {
                  onButtonExtraClick &&
                    onButtonExtraClick(
                      input1Value,
                      setInput1Value,
                      input2Value,
                      setInput2Value,
                      input3Value,
                      setInput3Value,
                    );
                }}
              >
                {buttonExtraText}
              </Button>
            )}
            <Button
              color={buttonTwoColor || 'success'}
              isLoading={buttonTwoLoading}
              onPress={async () => {
                if (confirmAction) {
                  setShowConfirmation(true);
                  return;
                }
                setButtonTwoLoading(true);
                await onButtonTwoClick(input1Value, input2Value, input3Value);
                setButtonTwoLoading(false);
                if (clearOnSubmit) {
                  setInput1Value('');
                  setInput2Value('');
                  setInput3Value('');
                }
              }}
            >
              {buttonTwoText}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}

export function PopupWithConfirmation({
  open,
  headerTitle = 'Title',
  bodyText = 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nullam pulvinar risus non risus hendrerit venenatis. Pellentesque sit amet hendrerit risus, sed porttitor quam.',
  buttonOneText = 'Cancel',
  buttonTwoText = 'Submit',
  onButtonOneClick,
  onButtonTwoClick,
  withTimerOfNSeconds = 3,
}: {
  open: boolean;
  headerTitle?: string;
  bodyText?: string;
  buttonOneText?: string;
  buttonTwoText?: string;
  onButtonOneClick?: any;
  onButtonTwoClick: any;
  withTimerOfNSeconds?: number | null; // nullable number for timer duration
}) {
  const { isOpen, onOpenChange, onClose } = useDisclosure({
    isOpen: open,
    onClose: onButtonOneClick,
  });

  const [isSubmitDisabled, setIsSubmitDisabled] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(withTimerOfNSeconds || 0);

  useEffect(() => {
    let intervalId: NodeJS.Timeout;

    if (withTimerOfNSeconds !== null) {
      setIsSubmitDisabled(true);
      setSecondsLeft(withTimerOfNSeconds || 0);

      intervalId = setInterval(() => {
        setSecondsLeft((prev) => {
          if (prev <= 1) {
            clearInterval(intervalId);
            setIsSubmitDisabled(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => clearInterval(intervalId);
  }, [withTimerOfNSeconds, isOpen]);

  return (
    <Modal
      backdrop="opaque"
      isOpen={isOpen}
      onClose={onClose}
      onOpenChange={onOpenChange}
      classNames={{
        backdrop:
          'bg-gradient-to-t from-zinc-900 to-zinc-900/10 backdrop-opacity-20',
      }}
    >
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader className="flex flex-col gap-1">
              {headerTitle}
            </ModalHeader>
            <ModalBody>
              <p>{bodyText}</p>
            </ModalBody>
            <ModalFooter>
              <Button
                color="default"
                variant="light"
                onPress={() => {
                  onButtonOneClick();
                  onClose();
                }}
              >
                {buttonOneText}
              </Button>
              <Button
                variant={isSubmitDisabled ? 'flat' : 'ghost'}
                color="danger"
                isDisabled={isSubmitDisabled}
                onPress={() => {
                  if (isSubmitDisabled) return;
                  onButtonTwoClick();
                  onClose();
                }}
              >
                {isSubmitDisabled
                  ? `${buttonTwoText} (${secondsLeft}s)`
                  : buttonTwoText}
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
}

export function PopupWithDropdownMenuInput({
  open,
  headerTitle = 'Title',
  dropdownMenuButtonText = 'First Input Label',
  buttonOneText = 'Cancel',
  buttonTwoText = 'Submit',
  onButtonOneClick,
  onButtonTwoClick,
  buttonOneColor = 'default',
  buttonTwoColor = 'success',
  confirmAction = false,
  items,
}: {
  open: boolean;
  headerTitle?: string;
  dropdownMenuButtonText: string;
  buttonOneText?: string;
  buttonTwoText?: string;
  onButtonOneClick?: any;
  onButtonTwoClick: any;
  buttonOneColor?:
    | 'default'
    | 'success'
    | 'danger'
    | 'primary'
    | 'secondary'
    | 'warning'
    | undefined;
  buttonTwoColor?:
    | 'default'
    | 'success'
    | 'danger'
    | 'primary'
    | 'secondary'
    | 'warning'
    | undefined;
  confirmAction?: boolean;
  items: { key: string; label: string }[];
}) {
  const { isOpen, onOpenChange } = useDisclosure({
    isOpen: open,
  });
  const [showConfirmation, setShowConfirmation] = useState(
    false && confirmAction,
  );

  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set([]));
  const selectedValue = useMemo(
    () => Array.from(selectedKeys).join(', ').replaceAll('_', ' '),
    [selectedKeys],
  );
  const [buttonTwoLoading, setButtonTwoLoading] = useState(false);

  return (
    <>
      <PopupWithConfirmation
        open={showConfirmation}
        headerTitle={headerTitle + '?'}
        bodyText="Are you sure you want to continue?"
        buttonOneText="No"
        buttonTwoText="Yes"
        onButtonOneClick={() => {
          setShowConfirmation(false);
        }}
        onButtonTwoClick={() => {
          setShowConfirmation(false);
          onButtonTwoClick(selectedValue);
        }}
      />
      <Modal
        backdrop="opaque"
        onOpenChange={onOpenChange}
        isOpen={isOpen}
        placement="top-center"
        size="lg"
        shadow="lg"
        hideCloseButton
      >
        <ModalContent>
          <ModalHeader className="flex flex-col gap-1">
            {headerTitle}
          </ModalHeader>
          <ModalBody>
            <Dropdown closeOnSelect shouldBlockScroll={false}>
              <DropdownTrigger>
                <Button
                  variant="bordered"
                  className="flex justify-between w-full"
                  endContent={
                    <IoChevronDownOutline className="text-2xl text-default-400 pointer-events-none flex-shrink-0" />
                  }
                >
                  {' '}
                  {selectedValue.length > 0
                    ? items.find((item) => item.key === selectedValue)?.label
                    : dropdownMenuButtonText}
                </Button>
              </DropdownTrigger>
              <DropdownMenu
                disallowEmptySelection={false}
                aria-label="Dynamic Actions"
                items={items}
              >
                {(item) => (
                  <DropdownItem
                    key={item.key}
                    color={item.key === 'delete' ? 'danger' : 'default'}
                    className={item.key === 'delete' ? 'text-danger' : ''}
                    onClick={() => {
                      setSelectedKeys(new Set([item.key]));
                    }}
                    closeOnSelect
                  >
                    {item.label}
                  </DropdownItem>
                )}
              </DropdownMenu>
            </Dropdown>
          </ModalBody>
          <ModalFooter>
            <Button
              color={buttonOneColor || 'default'}
              variant="flat"
              onPress={() => {
                onButtonOneClick && onButtonOneClick();
              }}
            >
              {buttonOneText}
            </Button>
            <Button
              color={buttonTwoColor || 'success'}
              isLoading={buttonTwoLoading}
              onPress={async () => {
                if (confirmAction) {
                  setShowConfirmation(true);
                  return;
                }
                setButtonTwoLoading(true);
                await onButtonTwoClick(selectedValue);
                setButtonTwoLoading(false);
                setSelectedKeys(new Set([]));
              }}
            >
              {buttonTwoText}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}

// This is a popup component that has a dropdown menu and a text area input, each selected item from the dropdown menu will be added to the text area input in a new line.
export function PopupWithDropdownMenuAndTextArea({
  open,
  headerTitle = 'Title',
  dropdownMenuButtonText = 'First Input Label',
  buttonOneText = 'Cancel',
  buttonTwoText = 'Submit',
  onButtonOneClick,
  onButtonTwoClick,
  buttonOneColor = 'default',
  buttonTwoColor = 'success',
  confirmAction = false,
  items,
  multipleInputs = false,
}: {
  open: boolean;
  headerTitle?: string;
  dropdownMenuButtonText: string;
  buttonOneText?: string;
  buttonTwoText?: string;
  onButtonOneClick?: any;
  onButtonTwoClick: any;
  buttonOneColor?:
    | 'default'
    | 'success'
    | 'danger'
    | 'primary'
    | 'secondary'
    | 'warning'
    | undefined;
  buttonTwoColor?:
    | 'default'
    | 'success'
    | 'danger'
    | 'primary'
    | 'secondary'
    | 'warning'
    | undefined;
  confirmAction?: boolean;
  items: { key: string; label: string }[];
  multipleInputs?: boolean;
}) {
  const { isOpen, onOpenChange } = useDisclosure({
    isOpen: open,
  });
  const [showConfirmation, setShowConfirmation] = useState(
    false && confirmAction,
  );

  const [lines, setLines] = useState('');

  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set([]));
  const selectedValue = useMemo(
    () =>
      items
        .filter((item) => selectedKeys.has(item.key))
        .map((item) => item.label)
        .join(',\n ')
        .replaceAll('_', ' '),
    [selectedKeys],
  );
  const [buttonTwoLoading, setButtonTwoLoading] = useState(false);

  return (
    <>
      <PopupWithConfirmation
        open={showConfirmation}
        headerTitle={headerTitle + '?'}
        bodyText="Are you sure you want to continue?"
        buttonOneText="No"
        buttonTwoText="Yes"
        onButtonOneClick={() => {
          setShowConfirmation(false);
        }}
        onButtonTwoClick={() => {
          setShowConfirmation(false);
          onButtonTwoClick(selectedValue);
        }}
      />
      <Modal
        backdrop="opaque"
        onOpenChange={onOpenChange}
        isOpen={isOpen}
        placement="top-center"
        size="lg"
        shadow="lg"
        hideCloseButton
      >
        <ModalContent>
          <ModalHeader className="flex flex-col gap-1">
            {headerTitle}
          </ModalHeader>
          <ModalBody>
            <Dropdown closeOnSelect shouldBlockScroll={false} showArrow>
              <DropdownTrigger>
                <Button
                  variant="bordered"
                  className="flex justify-between w-full"
                  style={{
                    height: 'fit-content',
                    minHeight: '3rem',
                    whiteSpace: 'normal',
                    wordWrap: 'break-word',
                  }}
                  endContent={
                    <IoChevronDownOutline className="text-2xl text-default-400 pointer-events-none flex-shrink-0" />
                  }
                >
                  {selectedValue.length > 0
                    ? selectedValue
                    : dropdownMenuButtonText}
                </Button>
              </DropdownTrigger>
              <DropdownMenu
                disallowEmptySelection={false}
                aria-label="Dynamic Actions"
                items={items}
                selectionMode={multipleInputs ? 'multiple' : 'single'}
                closeOnSelect={multipleInputs ? false : true}
              >
                {(item) => (
                  <DropdownItem
                    key={item.key}
                    selectedIcon={() => {
                      return selectedKeys.has(item.key) ? (
                        <IoCheckmark />
                      ) : null;
                    }}
                    onClick={() => {
                      const newKeys = new Set(selectedKeys);
                      if (selectedKeys.has(item.key)) {
                        newKeys.delete(item.key);
                      } else {
                        newKeys.add(item.key);
                        setLines((prev) => prev + item.label + ': \n\n\n');
                      }
                      setSelectedKeys(newKeys);
                    }}
                    closeOnSelect
                  >
                    {item.label}
                  </DropdownItem>
                )}
              </DropdownMenu>
            </Dropdown>
            <Textarea
              minRows={10}
              maxRows={40}
              label="Description"
              labelPlacement="outside"
              placeholder="Enter rejection reasons"
              defaultValue=""
              value={lines}
              onChange={(e) => setLines(e.target.value)}
            />
          </ModalBody>
          <ModalFooter
            className="flex justify-between
          align-center gap-3
          "
          >
            <Button
              variant="light"
              onPress={() => {
                setSelectedKeys(new Set([]));
                setLines('');
              }}
            >
              Clear all
              <XCircle className="text-2xl text-default-400 pointer-events-none flex-shrink-0" />
            </Button>
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                gap: '0.1rem',
              }}
            >
              <Button
                color={buttonOneColor || 'default'}
                variant="flat"
                onPress={() => {
                  onButtonOneClick && onButtonOneClick();
                }}
              >
                {buttonOneText}
              </Button>
              <div style={{ width: '1rem' }} />
              <Button
                color={buttonTwoColor || 'success'}
                isLoading={buttonTwoLoading}
                onPress={async () => {
                  if (confirmAction) {
                    setShowConfirmation(true);
                    return;
                  }
                  setButtonTwoLoading(true);
                  await onButtonTwoClick(lines);
                  setButtonTwoLoading(false);
                  setSelectedKeys(new Set([]));
                  setLines('');
                }}
              >
                {buttonTwoText}
              </Button>
            </div>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}
